import { TechemTheme } from '@techem/techem-theme';
import VersionTag from 'presentation/components/version/VersionTag';
import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { styled } from 'styletron-react';
import { md, minBreakpoints } from 'presentation/theme/mediaQueries';
import { useMediaQuery } from 'tools/hooks/useMediaQuery';
import { TrackerUtil } from 'tracking/TrackerUtil';
import { FooterLogo } from '../../presentation/components/layout/footer/FooterLogo';
import { environmentService } from '../../config/di/container';

const LinksWrapper = styled(Row, {
  fontSize: '0.875rem',
  [md]: { fontSize: '1rem' }
});

const WrapperView = styled('div', {
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: TechemTheme.colors.backgroundSecondary,
  zIndex: 1000
});

const FooterView = styled(Col, {
  height: '100px',
  backgroundColor: TechemTheme.colors.backgroundPrimary
});

const ExternalLink = styled('a', {
  color: TechemTheme.colors.contentSecondary,
  textDecoration: 'none'
});

const Footer = () => {
  var { t } = useTranslation();
  const isMdOrAbove = useMediaQuery(`(min-width: ${minBreakpoints.md}px)`);

  const pipelineId = environmentService.getPipelineId();
  const pipelineCreatedAt = environmentService.getPipelineCreatedAt();

  const linkSpacer = isMdOrAbove ? (
    <Col xs={{ span: 'auto', order: 0 }} className="px-2 d-none d-md-block"></Col>
  ) : (
    <></>
  );

  return (
    <WrapperView data-testid={'footer'} id="footer">
      <Container>
        <Row className="justify-content-center">
          <FooterView xs="auto" className="d-none d-md-block">
            <div className="d-flex flex-column w-100 h-100">
              <FooterLogo />
            </div>
          </FooterView>

          <Col className="px-0 d-none d-md-block" xs></Col>

          <FooterView xs="auto" className="d-flex px-0">
            <LinksWrapper className="justify-content-center h-auto mx-0 my-auto">
              <Col
                xs={{ span: 'auto', order: 0 }}
                md={{ span: 'auto', order: 0 }}
                className="px-2 text-end"
              >
                <ExternalLink
                  href="https://www.techem.de/impressum"
                  aria-label={'Impressum'}
                  onClick={() => {
                    TrackerUtil.trackLinkClick('footer_link_legal-notice', 'link_legal-notice');
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-gi="footer-link impressum"
                >
                  {t('footerImprintLabelText')}
                </ExternalLink>
              </Col>

              {linkSpacer}

              <Col
                xs={{ span: 'auto', order: 4 }}
                md={{ span: 'auto', order: 0 }}
                className="px-2 pt-2 mt-1 pt-md-0 mt-md-0 text-end"
              >
                <Link
                  style={{
                    color: TechemTheme.colors.contentSecondary,
                    textDecoration: 'none'
                  }}
                  target="_blank"
                  to="/agb"
                  data-gi="footer-link agb"
                  aria-label={'Allgemeine Geschäftsbedingungen'}
                  onClick={() => {
                    TrackerUtil.trackLinkClick(
                      'footer_link_terms-and-conditions',
                      'link_terms-and-conditions'
                    );
                  }}
                >
                  {t('footerTermsAndConditionsLabelText')}
                </Link>
              </Col>

              {linkSpacer}

              <Col xs={{ span: 'auto', order: 1 }} md={{ span: 'auto', order: 0 }} className="px-2">
                <Link
                  style={{
                    color: TechemTheme.colors.contentSecondary,
                    textDecoration: 'none'
                  }}
                  target="_blank"
                  to="/widerruf"
                  data-gi="footer-link widerruf"
                  aria-label={'Widerruf'}
                  onClick={() => {
                    TrackerUtil.trackLinkClick('footer_link_revocation', 'link_revocation');
                  }}
                >
                  {t('footerDisclaimerLabelText')}
                </Link>
              </Col>

              {linkSpacer}

              <Col xs={{ span: 'auto', order: 2 }} md={{ span: 'auto', order: 0 }} className="px-2">
                <Link
                  style={{
                    color: TechemTheme.colors.contentSecondary,
                    textDecoration: 'none'
                  }}
                  target="_blank"
                  to="https://www.techem.com/de/de/datenschutzhinweis"
                  data-gi="footer-link datenschutz"
                  aria-label={'Datenschutz'}
                  onClick={() => {
                    TrackerUtil.trackLinkClick('footer_link_data-privacy', 'link_data-privacy');
                  }}
                >
                  {t('footerDataPrivacyLabelText')}
                </Link>
              </Col>

              {linkSpacer}

              <Col
                xs={{ span: 'auto', order: 2 }}
                md={{ span: 'auto', order: 0 }}
                className="px-2 pt-2 mt-1 pt-md-0 mt-md-0 text-end"
              >
                <Link
                  style={{
                    color: TechemTheme.colors.contentSecondary,
                    textDecoration: 'none'
                  }}
                  target="_blank"
                  to="https://www.techem.com/de/de/services/erklaerung-zur-barrierefreiheit"
                  data-gi="footer-link barrierefreiheit"
                  aria-label={'Erklärung zur Barrierefreiheit'}
                  onClick={() => {
                    TrackerUtil.trackLinkClick('footer_link_accessibility', 'link_accessibility');
                  }}
                >
                  {t('footerAccessibilityLabelText')}
                </Link>
              </Col>

              {linkSpacer}

              <Col xs={{ span: 'auto', order: 3 }} className="d-md-none w-100" />

              <Col
                xs={{ span: 'auto', order: 5 }}
                md={{ span: 'auto', order: 0 }}
                className="px-2 pt-2 mt-1 pt-md-0 mt-md-0 text-end"
              >
                <ExternalLink
                  href="https://www.techem.com/de/de/services/online-kuendigung"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-gi="footer-link online-kuendigung"
                  aria-label={'Online Kündigung'}
                  onClick={() => {
                    TrackerUtil.trackLinkClick('footer_link_termination', 'link_termination');
                  }}
                >
                  {t('footerTerminationLabelText')}
                </ExternalLink>
              </Col>
            </LinksWrapper>
          </FooterView>
        </Row>
      </Container>
      {pipelineId && pipelineCreatedAt && (
        <div className="position-relative">
          <VersionTag
            pipelineId={pipelineId}
            pipelineDate={pipelineCreatedAt}
            className="position-absolute text-center"
            style={{
              left: '5%',
              transform: 'translate(-5%, 0)',
              bottom: '8px'
            }}
          />
        </div>
      )}
    </WrapperView>
  );
};
export default Footer;
